import flatMapShim from 'array.prototype.flatmap/shim';

import { isPlainObject } from '@genially/commons';

flatMapShim();

export interface Task<T> {
  value: T;
  finished: boolean;
}

export interface TasksObject {
  [id: string]: Task<unknown>;
}

export type TaskRef = string;

// we use https:// as prefix because if we don't the server will try
// to convert it to another url :(
const taskRefStringPrefix = 'https://$task/';

export function extractTaskRef(value: unknown): string | undefined {
  if (typeof value === 'string' && value.startsWith(taskRefStringPrefix)) {
    return value.slice(taskRefStringPrefix.length);
  }
  return undefined;
}

export function isTaskRef(value: unknown): value is TaskRef {
  return !!extractTaskRef(value);
}

export function toTaskRef(id: string): string {
  return `${taskRefStringPrefix}${id}`;
}

function resolveTaskRef<T>(
  valueOrTaskRef: T | TaskRef,
  tasksObject: TasksObject,
  processUnfinished: boolean,
): T | TaskRef {
  const taskRef = extractTaskRef(valueOrTaskRef);
  if (!taskRef) {
    return valueOrTaskRef as T;
  }

  const task = tasksObject[taskRef] as Task<T | TaskRef>;
  if (!task) {
    throw new Error(`could not resolve task "${taskRef}"`);
  }

  if (!task.finished && !processUnfinished) {
    // leave task ref as is since it is not finished
    return valueOrTaskRef;
  }

  return resolveTaskRef<T>(task.value, tasksObject, processUnfinished);
}

/**
 * Given a structure it will resolve all task references to its actual values.
 *
 * @param data Structure to resolve.
 * @param tasksObject Object with task statuses.
 * @param processUnfinished Also resolve unfinished tasks.
 * @returns Resolved structure.
 */
export function resolveAllTaskRefs<T>(
  data: T,
  tasksObject: TasksObject | undefined,
  processUnfinished: boolean,
): T {
  if (!tasksObject) {
    // most probably all values are already resolved
    return data;
  }

  if (Array.isArray(data)) {
    let oneChanged = false;

    const arr = data as unknown[];
    const newArr = arr.map(oldVal => {
      const newVal = resolveAllTaskRefs(oldVal, tasksObject, processUnfinished);
      if (newVal !== oldVal) {
        oneChanged = true;
      }
      return newVal;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) as any;

    return oneChanged ? newArr : data;
  }

  if (extractTaskRef(data)) {
    return resolveTaskRef<T>(data, tasksObject, processUnfinished) as T;
  }

  if (isPlainObject(data)) {
    const newObj: Record<string, unknown> = {};

    let oneChanged = false;
    Object.keys(data).forEach(k => {
      const oldVal = (data as Record<string, unknown>)[k];
      const newVal = resolveAllTaskRefs(oldVal, tasksObject, processUnfinished);

      newObj[k] = newVal;

      if (newVal !== oldVal) {
        oneChanged = true;
      }
    });

    return oneChanged ? (newObj as T) : data;
  }

  // some other simple value
  return data;
}

/**
 * Given a value it will find the tasks needed to resolve the value.
 */
function findTaskRefs(
  valueOrTaskRef: unknown | TaskRef,
  tasksObject: TasksObject,
  foundTaskRefs: TaskRef[],
): void {
  const taskRef = extractTaskRef(valueOrTaskRef);
  if (!taskRef) {
    return;
  }

  const task = tasksObject[taskRef] as Task<unknown | TaskRef>;
  if (!task) {
    throw new Error(`could not resolve task "${taskRef}"`);
  }

  foundTaskRefs.push(taskRef);
  findTaskRefs(task.value, tasksObject, foundTaskRefs);
}

/**
 * Given a structure it will find all the tasks referenced by such structure.
 */
function findAllTaskRefs<T>(
  data: T,
  tasksObject: TasksObject,
  foundTaskRefs: TaskRef[],
): void {
  if (Array.isArray(data)) {
    const arr = data as unknown[];
    arr.forEach(v => findAllTaskRefs(v, tasksObject, foundTaskRefs));
  } else if (extractTaskRef(data)) {
    findTaskRefs(data, tasksObject, foundTaskRefs);
  } else if (isPlainObject(data)) {
    Object.values(data).forEach(v => {
      findAllTaskRefs(v, tasksObject, foundTaskRefs);
    });
  }

  // else some other simple value
}

/**
 * Given a structure it will resolve all finished task references to its actual values and
 * return a new tasks object with the unfinished ones.
 *
 * @param data Structure to resolve.
 * @param tasksObject Object with task statuses.
 * @returns Resolved structure and unfinished tasks object.
 */
export function resolveAllTaskRefsAndExtractUnfinished<T>(
  data: T,
  tasksObject: TasksObject | undefined,
): { data: T; unfinishedTasksObject: TasksObject | undefined } {
  if (!tasksObject) {
    // most probably all values are already resolved
    return { data, unfinishedTasksObject: undefined };
  }

  const newData = resolveAllTaskRefs(data, tasksObject, false);

  const unfinishedTaskRefs: string[] = [];
  findAllTaskRefs(newData, tasksObject, unfinishedTaskRefs);

  let unfinishedTasksObject: TasksObject | undefined;
  if (unfinishedTaskRefs.length > 0) {
    unfinishedTasksObject = {};
    unfinishedTaskRefs.forEach(taskRef => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unfinishedTasksObject![taskRef] = tasksObject[taskRef];
    });
  }

  return {
    data: newData,
    unfinishedTasksObject,
  };
}

/**
 * Given a structure it will pick all used task references and return an object
 * with only the used tasks.
 *
 * @param data Structure to resolve.
 * @param tasksObject Object with task statuses.
 * @returns Used task statuses.
 */
export function pickUsedTasks<T>(data: T, tasksObject: TasksObject): TasksObject {
  const usedTaskRefs: string[] = [];
  findAllTaskRefs(data, tasksObject, usedTaskRefs);

  const usedTaskObject: TasksObject = {};
  usedTaskRefs.forEach(taskRef => {
    usedTaskObject[taskRef] = tasksObject[taskRef];
  });

  return usedTaskObject;
}
